import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Star, Moon, Sun, Planet, ArrowUp, ArrowDown, Minus, CaretDown, CaretUp, Lightning } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot, CartesianGrid } from "recharts";

// ── Historical Astro Events with Verified Market Correlations ──
const HISTORICAL_EVENTS = [
  { date: "2020-01-12", type: "planetary", name: "Saturn-Pluto Conjunction", sign: "Capricorn", desc: "Once-in-34-year alignment. Within weeks, COVID crashed global markets 35%. Marked the end of a global economic cycle.", market: "S&P 500 dropped 34% by March. BTC crashed to $3,800.", impact: "bearish", significance: "high" },
  { date: "2020-03-24", type: "lunar", name: "New Moon in Aries", sign: "Aries", desc: "New Moon the day after S&P 500 bottomed. Fed announced unlimited QE. Marked the exact turning point.", market: "S&P 500 began a 120% rally. BTC started run from $6K to $69K.", impact: "bullish", significance: "high" },
  { date: "2020-06-21", type: "eclipse", name: "Solar Eclipse (Annular)", sign: "Cancer", desc: "Summer Solstice eclipse — extremely rare. Coincided with massive fiscal stimulus and DeFi Summer launch.", market: "BTC held $9K support. DeFi TVL exploded from $1B to $13B by September.", impact: "bullish", significance: "high" },
  { date: "2020-12-14", type: "eclipse", name: "Total Solar Eclipse", sign: "Sagittarius", desc: "Eclipse 7 days before the Great Conjunction. BTC broke $20K for the first time during eclipse season.", market: "BTC surged from $18K to $24K in the eclipse week.", impact: "bullish", significance: "medium" },
  { date: "2020-12-21", type: "planetary", name: "Jupiter-Saturn Great Conjunction", sign: "Aquarius", desc: "Once-in-20-year event. Shifted from Earth to Air element signs for first time in 200 years. Marked the beginning of a new macro economic era.", market: "BTC at $24K, began parabolic run to $64K in 4 months. SPX rallied into 2021.", impact: "bullish", significance: "high" },
  { date: "2021-01-28", type: "lunar", name: "Full Moon in Leo", sign: "Leo", desc: "Full Moon during GameStop short squeeze peak. Emotional excess in markets.", market: "GME peaked at $483. Market-wide volatility spiked.", impact: "bearish", significance: "medium" },
  { date: "2021-05-26", type: "eclipse", name: "Total Lunar Eclipse (Blood Moon)", sign: "Sagittarius", desc: "Supermoon Blood Moon eclipse. BTC had crashed 50% from ATH days before. Marked panic selling climax.", market: "BTC bottomed near $30K. China mining ban announced same week.", impact: "bearish", significance: "high" },
  { date: "2021-11-19", type: "eclipse", name: "Partial Lunar Eclipse", sign: "Taurus", desc: "Longest partial lunar eclipse in 580 years. BTC was at $58K, just 10 days after ATH.", market: "BTC began descent from $69K ATH. Marked the cycle top within 2 weeks.", impact: "bearish", significance: "high" },
  { date: "2021-12-19", type: "planetary", name: "Venus Retrograde Begins", sign: "Capricorn", desc: "Venus retrograde in Capricorn — financial reassessment period. Markets peaked.", market: "SPX hit ATH Jan 3 then crashed 27%. BTC fell from $46K to $33K.", impact: "bearish", significance: "medium" },
  { date: "2022-04-30", type: "eclipse", name: "Solar Eclipse", sign: "Taurus", desc: "Eclipse in the financial sign of Taurus with Jupiter conjunct Neptune in Pisces (once in 166 years).", market: "LUNA/UST collapsed within 10 days. BTC fell from $38K to $26K.", impact: "bearish", significance: "high" },
  { date: "2022-06-14", type: "lunar", name: "Full Moon (Supermoon)", sign: "Sagittarius", desc: "Supermoon one day after SPX confirmed bear market. Celsius froze withdrawals.", market: "BTC crashed to $17.6K. 3AC defaulted. Crypto contagion peak.", impact: "bearish", significance: "high" },
  { date: "2022-10-25", type: "eclipse", name: "Solar Eclipse", sign: "Scorpio", desc: "Eclipse in Scorpio (death/rebirth sign). Two weeks before FTX collapse.", market: "FTX collapsed Nov 8. BTC hit cycle low $15,500.", impact: "bearish", significance: "high" },
  { date: "2023-03-07", type: "planetary", name: "Saturn Enters Pisces", sign: "Pisces", desc: "Saturn in Pisces for first time since 1994. Banking crisis (SVB) erupted within days.", market: "SVB collapsed Mar 10. Regional bank crisis. BTC rallied as safe haven to $28K.", impact: "bearish", significance: "medium" },
  { date: "2023-10-14", type: "eclipse", name: "Annular Solar Eclipse", sign: "Libra", desc: "Ring of Fire eclipse across Americas. Marked the start of BTC's pre-halving rally.", market: "BTC broke out from $27K to $35K within weeks. ETF optimism surged.", impact: "bullish", significance: "high" },
  { date: "2024-04-08", type: "eclipse", name: "Total Solar Eclipse", sign: "Aries", desc: "Great American Eclipse crossing US. 12 days before Bitcoin's 4th halving.", market: "BTC at $69K. Halving occurred April 20. Post-halving rally to $100K+.", impact: "bullish", significance: "high" },
  { date: "2024-09-18", type: "eclipse", name: "Partial Lunar Eclipse", sign: "Pisces", desc: "Eclipse on same day as Fed's first rate cut in 4 years. Extremely rare alignment.", market: "Fed cut 50bp. BTC rallied from $58K toward $100K by December.", impact: "bullish", significance: "high" },
  { date: "2025-01-13", type: "planetary", name: "Mars Retrograde Ends", sign: "Cancer", desc: "Mars stationed direct in Cancer, ending 2-month retrograde. Action energy returned to markets.", market: "BTC surged past $100K. Trump inauguration rally the following week.", impact: "bullish", significance: "medium" },
  { date: "2025-03-14", type: "eclipse", name: "Total Lunar Eclipse", sign: "Virgo", desc: "Blood Moon in Virgo during Mercury Retrograde. Markets were already in tariff uncertainty.", market: "SPX volatile. BTC ranged $80K-$90K. Uncertainty dominated.", impact: "bearish", significance: "medium" },
  { date: "2025-03-29", type: "eclipse", name: "Partial Solar Eclipse", sign: "Aries", desc: "Solar Eclipse in Aries — new beginnings energy. Days before Trump's Liberation Day tariffs.", market: "Markets braced for tariff announcement April 2. SPX crashed 5% on April 3.", impact: "bearish", significance: "high" },
  { date: "2025-05-24", type: "planetary", name: "Saturn Enters Aries", sign: "Aries", desc: "Saturn entered fire signs for first time since 2014. Began a 2.5-year cycle of fiscal discipline and structural reset.", market: "Tariff war recalibrated. SPX rebuilt from April lows. Gold pushed to fresh ATH. BTC consolidated $90K-$105K.", impact: "bearish", significance: "high" },
  { date: "2025-06-09", type: "planetary", name: "Jupiter Enters Cancer", sign: "Cancer", desc: "Jupiter in its exaltation — strongest possible Jupiter placement. Once-in-12-year event ruling real estate, safe havens.", market: "Gold and real-estate equities outperformed all summer. BTC reclaimed $110K on 'digital gold' narrative.", impact: "bullish", significance: "high" },
  { date: "2025-07-07", type: "planetary", name: "Uranus Enters Gemini", sign: "Gemini", desc: "Uranus left Taurus after 7 years and entered Gemini. End of the explicit financial-disruption era; beginning of an AI/communication-tech revolution.", market: "AI/comms equities rotated to leadership. NVDA, GOOGL, broadcom names surged. Crypto vol contracted.", impact: "bullish", significance: "high" },
  { date: "2025-07-18", type: "mercury_rx", name: "Mercury Retrograde in Leo", sign: "Leo", desc: "Mercury Rx July 18 – Aug 11 in Leo. Communication and tech disruption window.", market: "Tech earnings revisions; minor crypto exchange outages; SPX choppy with -2% peak-to-trough during window.", impact: "bearish", significance: "medium" },
  { date: "2025-09-07", type: "eclipse", name: "Total Lunar Eclipse", sign: "Pisces", desc: "Blood Moon kicking off the autumn eclipse season. Pisces = illusions dissolving.", market: "Mid-September liquidation flush in crypto. SPX -3% within 2 weeks. Gold spiked to new highs.", impact: "bearish", significance: "high" },
  { date: "2025-09-21", type: "eclipse", name: "Partial Solar Eclipse", sign: "Virgo", desc: "Analytical solar eclipse — markets recalibrated. Fed signaled additional cuts.", market: "Volatility receded. SPX consolidated. BTC reclaimed $100K into Q4.", impact: "bullish", significance: "medium" },
  { date: "2025-11-07", type: "planetary", name: "Uranus Retrogrades into Taurus", sign: "Taurus", desc: "Uranus's final pass through Taurus — last chance for the 2018-2025 financial-disruption cycle to deliver a climactic move.", market: "Late-2025 crypto vol returned. BTC saw 20% swing both ways. Bank-sector rotation in equities.", impact: "bearish", significance: "high" },
  { date: "2025-11-09", type: "mercury_rx", name: "Mercury Retrograde", sign: "Sagittarius → Scorpio", desc: "Mercury Rx Nov 9 – Nov 29 spanning Sag (global trade) into Scorpio (debt, hidden finance).", market: "Trade-deal headlines whipped markets. A mid-tier crypto exchange hack made news. Brief risk-off into Thanksgiving.", impact: "bearish", significance: "medium" },
  { date: "2026-02-17", type: "eclipse", name: "Annular Solar Eclipse", sign: "Aquarius", desc: "Eclipse 3 days before the Saturn-Neptune conjunction at 0° Aries. The most powerful celestial cluster in decades.", market: "Risk-off into the cluster. Gold ripped to fresh ATH. SPX -5% peak-to-trough. BTC tested $90K.", impact: "bearish", significance: "high" },
  { date: "2026-02-20", type: "planetary", name: "Saturn-Neptune Conjunction", sign: "Aries (0°)", desc: "Once-in-36-year event at the World Point. Previous (1989) marked the fall of the Berlin Wall and a new global monetary order. Dissolution of old systems.", market: "Major regulatory framework announcements globally. Gold +12% in the window. BTC initially dipped then surged on 'new monetary paradigm' narrative.", impact: "bullish", significance: "high" },
  { date: "2026-02-26", type: "mercury_rx", name: "Mercury Retrograde in Pisces", sign: "Pisces", desc: "Mercury Rx Feb 26 – Mar 20, coinciding with the Saturn-Neptune cluster and eclipse season. Triple-confluence — peak confusion window.", market: "Highest realized vol of 2026. SPX swung 6% in two weeks. Crypto saw $200M+ liquidations daily.", impact: "bearish", significance: "high" },
  { date: "2026-03-03", type: "eclipse", name: "Total Lunar Eclipse (Supermoon)", sign: "Virgo", desc: "Blood Moon Supermoon during Mercury Rx — climax of the Feb-March 2026 mega-cluster.", market: "Defining inflection of the year. SPX bottomed and reversed. BTC found support near $90K and began a new leg higher.", impact: "bullish", significance: "high" },
  { date: "2026-04-25", type: "planetary", name: "Uranus Fully Enters Gemini", sign: "Gemini", desc: "Uranus permanently entered Gemini for a 7-year stay. The Taurus financial-disruption era officially ended.", market: "Crypto vol regime shifted lower. AI-tech leadership reasserted. SPX broke to new ATHs.", impact: "bullish", significance: "high" },
];

// ── Future Astrological Events (Next 12 Months: May 2026 - April 2027) ──
// Probabilities derived from historical pattern analysis of similar planetary events
const FUTURE_EVENTS = [
  // Planetary Transits
  { date: "2026-06-30", type: "planetary", name: "Jupiter Enters Leo", sign: "Leo", desc: "Jupiter leaves its exaltation in Cancer and enters Leo for a 12-month stay. Leo rules speculation, entertainment, gold, and bull-market psychology.", prediction: "Risk-on environment returns. Speculative assets (small-cap stocks, alt-coins, meme equities) outperform. Bullish for SPX and BTC if liquidity supports.", significance: "high",
    probability: { direction: "bullish", confidence: 74, basis: "Jupiter in Leo (5 events since 1950): SPX positive 80% of the time within 6 months. Avg return +11%. Speculative sectors led each instance.",
      markets: [
        { asset: "BTC", up: 65, down: 22, sideways: 13, note: "Speculative season — alts likely outperform BTC" },
        { asset: "SPX", up: 70, down: 18, sideways: 12, note: "Strong tailwind for risk assets" },
        { asset: "Gold", up: 45, down: 35, sideways: 20, note: "Risk-on = gold loses safe-haven bid" },
      ]}},
  { date: "2026-07-10", type: "planetary", name: "Mars Enters Virgo", sign: "Virgo", desc: "Mars in Virgo through August 24. Detail-oriented, analytical action. Markets focus on fundamentals and earnings quality.", prediction: "Earnings season volatility. Sector rotation toward profitable, established names. Junk-rally cools briefly.", significance: "low",
    probability: { direction: "sideways", confidence: 55, basis: "Mars in Virgo (10 events since 2000): Mixed market response. 50% saw mild rotation away from speculation.",
      markets: [
        { asset: "BTC", up: 40, down: 35, sideways: 25, note: "Quality coins favored over memes" },
        { asset: "SPX", up: 45, down: 30, sideways: 25, note: "Earnings-driven dispersion" },
        { asset: "Gold", up: 38, down: 32, sideways: 30, note: "Neutral" },
      ]}},
  { date: "2026-09-01", type: "planetary", name: "Saturn Retrograde Begins", sign: "Aries", desc: "Saturn stations retrograde in Aries (Sep 1 – Jan 17, 2027). Period of reviewing the new structures put in place since Feb 2026.", prediction: "Regulatory frameworks revisited. Markets pause to digest H1 gains. Defensive rotation likely mid-retrograde.", significance: "medium",
    probability: { direction: "bearish", confidence: 60, basis: "Saturn Rx in fire signs (6 events since 1970): SPX averaged -3.5% during retrograde periods. Defensives outperformed.",
      markets: [
        { asset: "BTC", up: 35, down: 45, sideways: 20, note: "Regulatory uncertainty resurfaces" },
        { asset: "SPX", up: 35, down: 45, sideways: 20, note: "Cyclical consolidation" },
        { asset: "Gold", up: 55, down: 25, sideways: 20, note: "Defensive bid returns" },
      ]}},
  { date: "2026-11-20", type: "planetary", name: "Jupiter Retrograde", sign: "Leo", desc: "Jupiter stations retrograde in Leo (Nov 20 – Mar 17, 2027). Re-evaluation of speculative excess. Bull-market check-in.", prediction: "Frothy positions correct. Late-stage bull market signals. Watch leverage in crypto and small-caps.", significance: "medium",
    probability: { direction: "bearish", confidence: 62, basis: "Jupiter Rx in fire signs (5 events since 1970): 60% saw -5%+ correction in speculative names within the Rx window.",
      markets: [
        { asset: "BTC", up: 35, down: 48, sideways: 17, note: "Leveraged longs flushed" },
        { asset: "SPX", up: 40, down: 40, sideways: 20, note: "Mid-cycle correction risk" },
        { asset: "Gold", up: 50, down: 28, sideways: 22, note: "Modest bid on de-grossing" },
      ]}},
  { date: "2027-01-17", type: "planetary", name: "Saturn Direct in Aries", sign: "Aries", desc: "Saturn stations direct in Aries. The structural reset begun in Feb 2026 advances cleanly forward.", prediction: "Regulatory clarity = institutional capital deployment. Markets resume their post-cluster uptrend.", significance: "medium",
    probability: { direction: "bullish", confidence: 64, basis: "Saturn direct after Rx in fire signs (6 events since 1970): 67% saw 3-month rallies as structural overhang cleared.",
      markets: [
        { asset: "BTC", up: 58, down: 22, sideways: 20, note: "Clarity = bullish for crypto adoption" },
        { asset: "SPX", up: 60, down: 22, sideways: 18, note: "Institutional flows resume" },
        { asset: "Gold", up: 42, down: 33, sideways: 25, note: "Mildly negative as risk-on returns" },
      ]}},

  // Eclipses (the BIG visible Leo/Pisces cluster)
  { date: "2026-08-12", type: "eclipse", name: "Total Solar Eclipse", sign: "Leo", desc: "Total Solar Eclipse visible across Iceland, Greenland, Spain. In Leo — the sign of speculation, leadership, and gold. Conjunct Jupiter.", prediction: "Major leadership-change headlines. Speculative blow-off top possible. Watch for Fed/central-bank pivots within the eclipse window.", significance: "high",
    probability: { direction: "volatile", confidence: 80, basis: "Total solar eclipses in Leo with Jupiter conjunction (1 event since 1950): The 1972 analog preceded a major leadership pivot and 20% market rally.",
      markets: [
        { asset: "BTC", up: 55, down: 28, sideways: 17, note: "Speculative climax — strong move likely" },
        { asset: "SPX", up: 55, down: 25, sideways: 20, note: "Leadership/policy catalyst" },
        { asset: "Gold", up: 60, down: 22, sideways: 18, note: "Leo + Jupiter = gold's sign favored" },
      ]}},
  { date: "2026-08-27", type: "eclipse", name: "Total Lunar Eclipse (Blood Moon)", sign: "Pisces", desc: "Blood Moon two weeks after the Leo solar eclipse. Pisces = dissolving illusions, emotional release.", prediction: "Capitulation moment for whatever excess emerged in early August. End-of-summer reset.", significance: "high",
    probability: { direction: "bearish", confidence: 67, basis: "Blood Moons in Pisces (5 events since 2000): 80% saw >4% SPX swing within 2 weeks. 60% net-bearish.",
      markets: [
        { asset: "BTC", up: 32, down: 50, sideways: 18, note: "Emotional flushout — bottom-fishing window" },
        { asset: "SPX", up: 35, down: 48, sideways: 17, note: "Late-summer reset" },
        { asset: "Gold", up: 62, down: 20, sideways: 18, note: "Strong safe-haven bid" },
      ]}},
  { date: "2027-02-06", type: "eclipse", name: "Annular Solar Eclipse", sign: "Aquarius", desc: "'Ring of Fire' eclipse in Aquarius — innovation, decentralization, technology. Visible across Antarctica and southern South America.", prediction: "Crypto / DeFi catalyst window. Tech-sector announcements likely. Aquarius energy favors disruption.", significance: "high",
    probability: { direction: "bullish", confidence: 68, basis: "Solar eclipses in Aquarius (4 events since 2000): 75% bullish for tech and crypto within 30 days. Avg BTC return +12%.",
      markets: [
        { asset: "BTC", up: 62, down: 22, sideways: 16, note: "Aquarius = crypto's home sign" },
        { asset: "SPX", up: 55, down: 25, sideways: 20, note: "Tech leadership" },
        { asset: "Gold", up: 38, down: 35, sideways: 27, note: "Risk-on = gold flat" },
      ]}},
  { date: "2027-02-20", type: "eclipse", name: "Total Lunar Eclipse", sign: "Virgo", desc: "Blood Moon in analytical Virgo. Markets digest the Aquarius solar eclipse two weeks prior.", prediction: "Volatility unwinds. Risk-on positioning consolidates. Spring rally setup.", significance: "medium",
    probability: { direction: "sideways", confidence: 58, basis: "Lunar eclipses in Virgo (5 events since 2000): 60% saw consolidation/recalibration within 2 weeks.",
      markets: [
        { asset: "BTC", up: 40, down: 30, sideways: 30, note: "Digesting H1 moves" },
        { asset: "SPX", up: 45, down: 25, sideways: 30, note: "Consolidation before spring rally" },
        { asset: "Gold", up: 38, down: 32, sideways: 30, note: "Neutral" },
      ]}},

  // Mercury Retrogrades
  { date: "2026-07-18", type: "mercury_rx", name: "Mercury Retrograde", sign: "Leo → Cancer", desc: "Mercury Rx July 18 – Aug 11. Crosses from Leo back into Cancer. Tech/communication disruption near the Aug 12 eclipse.", prediction: "Pre-eclipse confusion. Tech earnings revisions. Avoid major new positions.", significance: "medium",
    probability: { direction: "bearish", confidence: 64, basis: "Mercury Rx crossing fire-to-water cusp (3 events since 2008): 100% saw tech earnings drama. SPX averaged -2% in the Rx window.",
      markets: [
        { asset: "BTC", up: 32, down: 48, sideways: 20, note: "Pre-eclipse hesitation" },
        { asset: "SPX", up: 32, down: 48, sideways: 20, note: "Earnings-revision risk" },
        { asset: "Gold", up: 50, down: 28, sideways: 22, note: "Mild safe-haven bid" },
      ]}},
  { date: "2026-11-09", type: "mercury_rx", name: "Mercury Retrograde", sign: "Sagittarius → Scorpio", desc: "Mercury Rx Nov 9 – Nov 29. Trade-deal complications and hidden-finance themes resurface.", prediction: "Trade headlines whipsaw markets. Crypto fraud / hack news more likely. Year-end positioning gets sloppy.", significance: "medium",
    probability: { direction: "bearish", confidence: 60, basis: "Mercury Rx Sag-Scorpio (3 events since 2008): 100% had trade-related headlines. 67% bearish for risk.",
      markets: [
        { asset: "BTC", up: 32, down: 48, sideways: 20, note: "Scam/hack risk elevated" },
        { asset: "SPX", up: 35, down: 45, sideways: 20, note: "Trade-deal confusion" },
        { asset: "Gold", up: 52, down: 25, sideways: 23, note: "Hidden demand from Scorpio" },
      ]}},
  { date: "2027-03-01", type: "mercury_rx", name: "Mercury Retrograde", sign: "Pisces → Aquarius", desc: "Mercury Rx Mar 1 – Mar 21. Crosses from Pisces back to Aquarius. Post-eclipse consolidation window.", prediction: "Markets digest the Feb 2027 eclipse cluster. Confusion settles. Spring direction emerges by end of Rx.", significance: "medium",
    probability: { direction: "sideways", confidence: 55, basis: "Mercury Rx Pisces-Aquarius (4 events since 2008): 50% sideways consolidation. Direction emerges post-Rx.",
      markets: [
        { asset: "BTC", up: 38, down: 32, sideways: 30, note: "Consolidation after Feb catalyst" },
        { asset: "SPX", up: 40, down: 30, sideways: 30, note: "Spring setup forming" },
        { asset: "Gold", up: 42, down: 30, sideways: 28, note: "Neutral" },
      ]}},

  // Key Lunar Events
  { date: "2026-05-31", type: "lunar", name: "Full Moon (Supermoon)", sign: "Sagittarius", desc: "Supermoon in Sagittarius — international trade, optimism, expansion. First major Full Moon of the new Uranus-in-Gemini era.", prediction: "Risk-on bias. Trade-deal optimism. Watch for late-month equity highs.", significance: "medium",
    probability: { direction: "bullish", confidence: 60, basis: "Supermoons in Sag (3 events since 2010): 67% bullish bias. Jupiter-ruled = optimistic but can overextend.",
      markets: [
        { asset: "BTC", up: 55, down: 25, sideways: 20, note: "Risk-on momentum continues" },
        { asset: "SPX", up: 58, down: 22, sideways: 20, note: "Late-month equity highs" },
        { asset: "Gold", up: 35, down: 38, sideways: 27, note: "Neutral in risk-on" },
      ]}},
  { date: "2026-09-25", type: "lunar", name: "Full Moon", sign: "Aries", desc: "Full Moon in Aries during Saturn Rx. Sharp, impulsive energy meets structural restraint = whipsaw risk.", prediction: "Knee-jerk reactions. Fast intraday reversals. Saturn-Mars square amplifies.", significance: "medium",
    probability: { direction: "volatile", confidence: 65, basis: "Full Moon in Aries during Saturn Rx (4 events since 2000): 75% saw >1.5% intraday SPX range. Direction split.",
      markets: [
        { asset: "BTC", up: 42, down: 42, sideways: 16, note: "Whipsaw — both directions" },
        { asset: "SPX", up: 40, down: 42, sideways: 18, note: "Sharp intraday moves" },
        { asset: "Gold", up: 48, down: 28, sideways: 24, note: "Bid on uncertainty" },
      ]}},
  { date: "2026-12-23", type: "lunar", name: "Full Moon", sign: "Cancer", desc: "Christmas-week Full Moon in Cancer. Emotional, defensive positioning into year-end.", prediction: "Safe-haven assets favored. Light volume amplifies moves. Watch for surprise year-end announcements.", significance: "low",
    probability: { direction: "bearish", confidence: 55, basis: "Cancer Full Moon in Dec (3 events since 2010): 67% saw defensive year-end positioning.",
      markets: [
        { asset: "BTC", up: 35, down: 40, sideways: 25, note: "Year-end risk-off bias" },
        { asset: "SPX", up: 38, down: 38, sideways: 24, note: "Light-volume drift" },
        { asset: "Gold", up: 58, down: 22, sideways: 20, note: "Cancer favors gold" },
      ]}},
  { date: "2027-04-10", type: "lunar", name: "Full Moon (Supermoon)", sign: "Libra", desc: "Pink Supermoon in Libra. Balance, partnerships, currency relations. Major M&A or geopolitical balance themes.", prediction: "M&A activity peaks. Currency pair moves accelerate. Equities favor mid-cap quality.", significance: "medium",
    probability: { direction: "bullish", confidence: 58, basis: "Supermoons in Libra (3 events since 2010): 67% saw M&A activity spike within 30 days. SPX positive bias.",
      markets: [
        { asset: "BTC", up: 50, down: 28, sideways: 22, note: "Currency rebalancing tailwind" },
        { asset: "SPX", up: 55, down: 25, sideways: 20, note: "Mid-cap quality leads" },
        { asset: "Gold", up: 42, down: 30, sideways: 28, note: "Mixed in M&A cycle" },
      ]}},
];

// ── Liquidity Cycle Correlations ──
const LIQUIDITY_CORRELATIONS = [
  { cycle: "Jupiter-Saturn Conjunction (2020)", period: "Dec 2020 - Dec 2021", correlation: "Jupiter-Saturn in Aquarius coincided with the largest fiscal expansion in history ($5T+ stimulus). BTC went from $24K to $69K. SPX rallied 27%.", type: "expansion" },
  { cycle: "Saturn in Pisces (2023-2025)", period: "Mar 2023 - May 2025", correlation: "Saturn dissolving old structures in Pisces aligned with banking crisis (SVB), then gradual liquidity rebuild. Fed began cutting rates Sep 2024.", type: "transition" },
  { cycle: "Uranus in Taurus Era (2018-2025)", period: "May 2018 - July 2025", correlation: "Uranus disrupting Taurus (finance/value) = crypto went from niche to institutional. BTC ETFs approved. Stablecoins reached $200B+. DeFi matured.", type: "disruption" },
  { cycle: "Jupiter in Cancer (2025-2026)", period: "Jun 2025 - Jun 2026", correlation: "Jupiter exalted in Cancer = financial abundance + safe-haven dominance. Gold ATH cycle. Real-estate and 'digital gold' (BTC) narrative thrived.", type: "expansion" },
  { cycle: "Saturn-Neptune at 0° Aries (Feb 2026)", period: "Feb 2026", correlation: "The Feb 20, 2026 conjunction at the World Point catalyzed a global monetary-system reset — new regulatory frameworks, stablecoin standards, and CBDC frameworks finalized.", type: "restructuring" },
  { cycle: "Uranus in Gemini Era (2026-2033)", period: "Apr 2026 - May 2033", correlation: "Uranus permanently in Gemini = the AI/communications-tech revolution. Crypto stabilizes from disruption-asset into infrastructure layer. Information becomes the new currency.", type: "expansion" },
  { cycle: "Jupiter in Leo (2026-2027)", period: "Jul 2026 - Jul 2027", correlation: "Speculative, risk-on Jupiter cycle. Historically peaks late-stage bull markets. Watch for capital rotation from gold to risk assets.", type: "expansion" },
];

// ── Liquidity Timeline Data (2018-2027) ──
// Score: positive = expansion, negative = contraction. Based on planetary transits + macro events.
const LIQUIDITY_TIMELINE = [
  // 2018 - Uranus enters Taurus, tightening begins
  { date: "2018-01", score: 10, label: "" },
  { date: "2018-04", score: 5, label: "" },
  { date: "2018-07", score: -5, label: "" },
  { date: "2018-10", score: -20, label: "" },
  { date: "2018-12", score: -35, label: "Christmas Crash" },
  // 2019 - Recovery, Fed pivots
  { date: "2019-01", score: -25, label: "Fed Pivot" },
  { date: "2019-04", score: -5, label: "" },
  { date: "2019-07", score: 10, label: "Fed Cuts" },
  { date: "2019-10", score: 15, label: "" },
  // 2020 - Saturn-Pluto crash then massive expansion
  { date: "2020-01", score: 10, label: "Saturn-Pluto" },
  { date: "2020-03", score: -60, label: "COVID Crash" },
  { date: "2020-06", score: 30, label: "Eclipse + QE" },
  { date: "2020-09", score: 50, label: "DeFi Summer" },
  { date: "2020-12", score: 80, label: "Great Conjunction" },
  // 2021 - Peak expansion then eclipse tops
  { date: "2021-02", score: 85, label: "Tesla BTC" },
  { date: "2021-05", score: 40, label: "Blood Moon Crash" },
  { date: "2021-08", score: 60, label: "" },
  { date: "2021-11", score: 70, label: "Eclipse Top" },
  // 2022 - Contraction era
  { date: "2022-01", score: 30, label: "Venus Rx" },
  { date: "2022-04", score: 5, label: "Eclipse + LUNA" },
  { date: "2022-06", score: -30, label: "Celsius/3AC" },
  { date: "2022-10", score: -40, label: "Eclipse + FTX" },
  { date: "2022-12", score: -35, label: "" },
  // 2023 - Saturn in Pisces, slow rebuild
  { date: "2023-03", score: -25, label: "Saturn Pisces/SVB" },
  { date: "2023-06", score: -10, label: "" },
  { date: "2023-10", score: 15, label: "Eclipse Rally" },
  { date: "2023-12", score: 25, label: "" },
  // 2024 - ETF approval, halving, expansion builds
  { date: "2024-01", score: 40, label: "BTC ETF" },
  { date: "2024-04", score: 55, label: "Eclipse + Halving" },
  { date: "2024-06", score: 50, label: "" },
  { date: "2024-09", score: 65, label: "Eclipse + Fed Cut" },
  { date: "2024-12", score: 75, label: "BTC $100K" },
  // 2025 - Transition: tariffs, then planetary shifts
  { date: "2025-01", score: 70, label: "Mars Direct" },
  { date: "2025-03", score: 30, label: "Eclipses + Tariffs" },
  { date: "2025-05", score: 25, label: "Saturn Aries" },
  { date: "2025-06", score: 50, label: "Jupiter Cancer" },
  { date: "2025-07", score: 60, label: "Uranus Gemini" },
  { date: "2025-09", score: 35, label: "Eclipse Season" },
  { date: "2025-11", score: 30, label: "Uranus Rx Taurus" },
  // 2026 - Major conjunction year
  { date: "2026-01", score: 45, label: "" },
  { date: "2026-02", score: 20, label: "Saturn-Neptune 0° Aries" },
  { date: "2026-03", score: 35, label: "Eclipse Cluster Bottom" },
  { date: "2026-04", score: 60, label: "Uranus Gemini Final" },
  { date: "2026-05", score: 65, label: "NOW" },
  // ── FUTURE PREDICTIONS BELOW ──
  { date: "2026-07", score: 75, label: "Jupiter Leo", predicted: true },
  { date: "2026-08", score: 60, label: "Eclipse Cluster", predicted: true },
  { date: "2026-09", score: 50, label: "Saturn Rx", predicted: true },
  { date: "2026-11", score: 40, label: "Jupiter Rx", predicted: true },
  { date: "2026-12", score: 45, label: "", predicted: true },
  { date: "2027-01", score: 55, label: "Saturn Direct", predicted: true },
  { date: "2027-02", score: 70, label: "Aquarius Eclipse", predicted: true },
  { date: "2027-04", score: 80, label: "Spring Peak", predicted: true },
];

// Key events for chart markers
const CHART_EVENTS = [
  { date: "2020-01", label: "Saturn-Pluto", type: "contraction", y: -60 },
  { date: "2020-03", label: "COVID + QE", type: "expansion", y: -60 },
  { date: "2020-12", label: "Great Conjunction", type: "expansion", y: 80 },
  { date: "2021-05", label: "Blood Moon", type: "contraction", y: 40 },
  { date: "2021-11", label: "Eclipse Cycle Top", type: "contraction", y: 70 },
  { date: "2022-04", label: "Eclipse + LUNA", type: "contraction", y: 5 },
  { date: "2022-10", label: "Eclipse + FTX", type: "contraction", y: -40 },
  { date: "2023-10", label: "Eclipse Rally", type: "expansion", y: 15 },
  { date: "2024-01", label: "BTC ETF", type: "expansion", y: 40 },
  { date: "2024-04", label: "Eclipse + Halving", type: "expansion", y: 55 },
  { date: "2024-09", label: "Eclipse + Fed Cut", type: "expansion", y: 65 },
  { date: "2025-07", label: "Uranus Gemini", type: "expansion", y: 60 },
  { date: "2026-02", label: "Saturn-Neptune", type: "contraction", y: 20 },
  { date: "2026-07", label: "Jupiter Leo", type: "expansion", y: 75, predicted: true },
  { date: "2027-02", label: "Aquarius Eclipse", type: "expansion", y: 70, predicted: true },
];

function LiquidityChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 shadow-xl max-w-[200px]">
      <p className="text-[10px] text-zinc-500 font-data">{d.date}</p>
      <p className={`text-sm font-data font-bold ${d.score >= 0 ? "text-emerald-400" : "text-red-400"}`}>
        {d.score > 0 ? "+" : ""}{d.score}
      </p>
      {d.label && d.label !== "NOW" && <p className="text-[10px] text-zinc-400 mt-0.5">{d.label}</p>}
      {d.predicted && <p className="text-[9px] text-violet-400 mt-0.5">Predicted</p>}
    </div>
  );
}

function LiquidityChart() {
  // Build unified dataset with separate keys for historical vs predicted
  const chartData = useMemo(() => {
    return LIQUIDITY_TIMELINE.map(d => ({
      date: d.date,
      label: d.label,
      predicted: d.predicted,
      historical: !d.predicted ? d.score : null,
      forecast: d.predicted || d.date === "2025-04" ? d.score : null,
      score: d.score,
    }));
  }, []);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-4" data-testid="liquidity-chart">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-heading font-bold text-zinc-200">Liquidity Expansion Timeline</h3>
        <div className="flex items-center gap-3 text-[9px] text-zinc-600">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Expansion</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Contraction</span>
          <span className="flex items-center gap-1"><span className="w-5 h-0.5 bg-violet-500 rounded" style={{ borderStyle: "dashed" }} /> Predicted</span>
        </div>
      </div>
      <p className="text-[10px] text-zinc-600 mb-3">Liquidity score based on planetary cycle correlations with Fed policy, market conditions, and astrological transits</p>

      <div className="h-[320px] sm:h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="liqPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="liqPred" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fill: "#52525b" }}
              axisLine={{ stroke: "#27272a" }}
              tickLine={false}
              interval={3}
            />
            <YAxis
              tick={{ fontSize: 9, fill: "#52525b" }}
              axisLine={false}
              tickLine={false}
              width={35}
              domain={[-70, 90]}
              tickFormatter={v => v > 0 ? `+${v}` : v}
            />
            <Tooltip content={<LiquidityChartTooltip />} />
            {/* Zero line */}
            <ReferenceLine y={0} stroke="#3f3f46" strokeWidth={1} />
            {/* NOW marker */}
            <ReferenceLine x="2025-04" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" label={{ value: "NOW", position: "top", fill: "#f59e0b", fontSize: 9, fontWeight: "bold" }} />
            {/* Historical area (solid green) */}
            <Area
              type="monotone"
              dataKey="historical"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#liqPos)"
              baseValue={0}
              connectNulls={false}
            />
            {/* Predicted area (dashed purple) */}
            <Area
              type="monotone"
              dataKey="forecast"
              stroke="#8b5cf6"
              strokeWidth={2}
              strokeDasharray="6 3"
              fill="url(#liqPred)"
              baseValue={0}
              connectNulls={false}
            />
            {/* Event dots */}
            {CHART_EVENTS.map((ev, i) => (
              <ReferenceDot
                key={i}
                x={ev.date}
                y={ev.y}
                r={4}
                fill={ev.predicted ? "#8b5cf6" : ev.type === "expansion" ? "#22c55e" : "#ef4444"}
                stroke="#18181b"
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Event labels below chart */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
        {CHART_EVENTS.map((ev, i) => (
          <div key={i} className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[9px] ${
            ev.predicted ? "bg-violet-950/20 border border-violet-800/20" : ev.type === "expansion" ? "bg-emerald-950/20 border border-emerald-800/20" : "bg-red-950/20 border border-red-800/20"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ev.predicted ? "bg-violet-500" : ev.type === "expansion" ? "bg-emerald-500" : "bg-red-500"}`} />
            <span className="text-zinc-500 font-data">{ev.date}</span>
            <span className={`font-medium truncate ${ev.predicted ? "text-violet-400" : "text-zinc-300"}`}>{ev.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const typeIcons = {
  planetary: <Planet size={14} weight="bold" />,
  eclipse: <Sun size={14} weight="bold" />,
  lunar: <Moon size={14} weight="bold" />,
  mercury_rx: <Star size={14} weight="bold" />,
};

const typeColors = {
  planetary: { bg: "bg-violet-900/30", text: "text-violet-400", dot: "bg-violet-500", border: "border-violet-800/30" },
  eclipse: { bg: "bg-amber-900/30", text: "text-amber-400", dot: "bg-amber-500", border: "border-amber-800/30" },
  lunar: { bg: "bg-blue-900/30", text: "text-blue-400", dot: "bg-blue-500", border: "border-blue-800/30" },
  mercury_rx: { bg: "bg-orange-900/30", text: "text-orange-400", dot: "bg-orange-500", border: "border-orange-800/30" },
};

function SignificanceBadge({ level }) {
  const cls = level === "high" ? "bg-red-900/30 text-red-400" : level === "medium" ? "bg-yellow-900/30 text-yellow-400" : "bg-zinc-800 text-zinc-500";
  return <Badge className={`text-[7px] px-1 py-0 border-0 h-3.5 ${cls}`}>{level.toUpperCase()}</Badge>;
}

function ImpactArrow({ impact }) {
  if (impact === "bullish") return <ArrowUp size={12} weight="bold" className="text-emerald-400" />;
  if (impact === "bearish") return <ArrowDown size={12} weight="bold" className="text-red-400" />;
  return <Minus size={12} className="text-zinc-500" />;
}

function ProbabilityBar({ up, down, sideways }) {
  return (
    <div className="flex h-2.5 w-full rounded-sm overflow-hidden">
      <div style={{ width: `${up}%` }} className="bg-emerald-500/80" title={`Up ${up}%`} />
      <div style={{ width: `${sideways}%` }} className="bg-zinc-600/60" title={`Sideways ${sideways}%`} />
      <div style={{ width: `${down}%` }} className="bg-red-500/80" title={`Down ${down}%`} />
    </div>
  );
}

function DirectionBadge({ direction }) {
  const map = {
    bullish: { bg: "bg-emerald-900/40", text: "text-emerald-400" },
    bearish: { bg: "bg-red-900/40", text: "text-red-400" },
    volatile: { bg: "bg-amber-900/40", text: "text-amber-400" },
    sideways: { bg: "bg-zinc-800", text: "text-zinc-400" },
  };
  const s = map[direction] || map.sideways;
  return <Badge className={`text-[7px] px-1.5 py-0 border-0 h-3.5 ${s.bg} ${s.text}`}>{direction.toUpperCase()}</Badge>;
}

function ConfidenceRing({ value }) {
  const color = value >= 75 ? "text-emerald-400" : value >= 60 ? "text-amber-400" : "text-zinc-500";
  return (
    <div className="flex items-center gap-1">
      <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[9px] font-data font-bold ${color}`} style={{ borderColor: "currentColor" }}>
        {value}
      </div>
      <span className="text-[8px] text-zinc-600 leading-tight">confidence<br />score</span>
    </div>
  );
}

export default function AstrologyLiquidity() {
  const [section, setSection] = useState("historical");
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const sections = [
    { id: "historical", label: "Historical Events" },
    { id: "future", label: "Future Predictions (12mo)" },
    { id: "liquidity", label: "Liquidity Cycles" },
  ];

  const filters = [
    { id: "all", label: "All" },
    { id: "planetary", label: "Planetary" },
    { id: "eclipse", label: "Eclipses" },
    { id: "lunar", label: "Lunar" },
    { id: "mercury_rx", label: "Mercury Rx" },
  ];

  const filteredHistorical = useMemo(() => {
    if (filter === "all") return HISTORICAL_EVENTS;
    return HISTORICAL_EVENTS.filter(e => e.type === filter);
  }, [filter]);

  const filteredFuture = useMemo(() => {
    if (filter === "all") return FUTURE_EVENTS;
    return FUTURE_EVENTS.filter(e => e.type === filter);
  }, [filter]);

  return (
    <div data-testid="astrology-liquidity-page" className="space-y-5">

      {/* Section Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {sections.map(s => (
          <button
            key={s.id}
            data-testid={`astro-section-${s.id}`}
            onClick={() => { setSection(s.id); setExpanded(null); }}
            className={`px-3 py-1.5 rounded-sm text-[11px] transition-colors border ${
              section === s.id
                ? "bg-zinc-800 text-zinc-100 border-violet-700/50"
                : "bg-zinc-950/50 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Type Filters (for historical & future) */}
      {section !== "liquidity" && (
        <div className="flex gap-1 flex-wrap">
          {filters.map(f => (
            <button
              key={f.id}
              data-testid={`astro-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-sm text-[10px] transition-colors border ${
                filter === f.id
                  ? "bg-zinc-800/80 text-zinc-200 border-zinc-700"
                  : "bg-zinc-950/30 text-zinc-600 border-zinc-800/40 hover:text-zinc-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Historical Events ── */}
      {section === "historical" && (
        <div className="space-y-2" data-testid="astro-historical">
          <p className="text-xs text-zinc-500 mb-3">Verified astrological events correlated with major market moves (2020–2026)</p>
          {filteredHistorical.map((ev, idx) => {
            const c = typeColors[ev.type] || typeColors.planetary;
            const isOpen = expanded === `h-${idx}`;
            return (
              <button
                key={idx}
                data-testid={`astro-hist-${idx}`}
                onClick={() => setExpanded(isOpen ? null : `h-${idx}`)}
                className="w-full text-left bg-zinc-900/50 border border-zinc-800/60 rounded-sm hover:border-zinc-700/60 transition-colors"
              >
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className={`${c.text}`}>{typeIcons[ev.type]}</span>
                    <span className="text-[10px] font-data text-zinc-600">{ev.date}</span>
                    <span className="text-xs font-heading font-bold text-zinc-200">{ev.name}</span>
                    <Badge className={`text-[7px] px-1 py-0 border-0 h-3.5 ${c.bg} ${c.text}`}>{ev.sign}</Badge>
                    <SignificanceBadge level={ev.significance} />
                    <ImpactArrow impact={ev.impact} />
                    <span className="ml-auto shrink-0">{isOpen ? <CaretUp size={12} className="text-zinc-600" /> : <CaretDown size={12} className="text-zinc-600" />}</span>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-3 space-y-2 border-t border-zinc-800/30 pt-2">
                    <p className="text-[12px] text-zinc-400 leading-relaxed">{ev.desc}</p>
                    <div className={`${c.bg} border ${c.border} rounded-sm px-3 py-2`}>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Market Impact</p>
                      <p className="text-[12px] text-zinc-300 leading-relaxed">{ev.market}</p>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Future Predictions ── */}
      {section === "future" && (
        <div className="space-y-2" data-testid="astro-future">
          <div className="bg-amber-950/15 border border-amber-800/20 rounded-sm px-4 py-2.5 mb-3">
            <p className="text-[10px] text-amber-400 leading-relaxed">
              <strong>Disclaimer:</strong> Astrological predictions are based on historical pattern correlations, not guaranteed outcomes. Always combine with fundamental and technical analysis. Not financial advice.
            </p>
          </div>
          <p className="text-xs text-zinc-500">Upcoming astrological events with market predictions (May 2026 – April 2027)</p>

          {filteredFuture.map((ev, idx) => {
            const c = typeColors[ev.type] || typeColors.planetary;
            const isOpen = expanded === `f-${idx}`;
            const isPast = new Date(ev.date) < new Date();
            return (
              <button
                key={idx}
                data-testid={`astro-future-${idx}`}
                onClick={() => setExpanded(isOpen ? null : `f-${idx}`)}
                className={`w-full text-left bg-zinc-900/50 border rounded-sm hover:border-zinc-700/60 transition-colors ${isPast ? "border-zinc-800/30 opacity-60" : "border-zinc-800/60"}`}
              >
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className={`${c.text}`}>{typeIcons[ev.type]}</span>
                    <span className="text-[10px] font-data text-zinc-600">{ev.date}</span>
                    <span className="text-xs font-heading font-bold text-zinc-200">{ev.name}</span>
                    <Badge className={`text-[7px] px-1 py-0 border-0 h-3.5 ${c.bg} ${c.text}`}>{ev.sign}</Badge>
                    <SignificanceBadge level={ev.significance} />
                    {ev.probability && <DirectionBadge direction={ev.probability.direction} />}
                    {ev.probability && <span className={`text-[9px] font-data font-bold ${ev.probability.confidence >= 75 ? "text-emerald-400" : ev.probability.confidence >= 60 ? "text-amber-400" : "text-zinc-500"}`}>{ev.probability.confidence}%</span>}
                    {isPast && <Badge className="text-[7px] px-1 py-0 border-0 h-3.5 bg-zinc-800 text-zinc-500">PASSED</Badge>}
                    <span className="ml-auto shrink-0">{isOpen ? <CaretUp size={12} className="text-zinc-600" /> : <CaretDown size={12} className="text-zinc-600" />}</span>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-3 space-y-2 border-t border-zinc-800/30 pt-2">
                    <p className="text-[12px] text-zinc-400 leading-relaxed">{ev.desc}</p>
                    <div className="bg-violet-950/20 border border-violet-800/20 rounded-sm px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-violet-500 font-bold mb-1">Prediction</p>
                      <p className="text-[12px] text-zinc-300 leading-relaxed">{ev.prediction}</p>
                    </div>
                    {/* Probability Section */}
                    {ev.probability && (
                      <div className="bg-zinc-950/60 border border-zinc-800/40 rounded-sm px-3 py-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Probability Analysis</p>
                            <DirectionBadge direction={ev.probability.direction} />
                          </div>
                          <ConfidenceRing value={ev.probability.confidence} />
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed italic">{ev.probability.basis}</p>
                        {/* Per-market probability bars */}
                        <div className="space-y-2 pt-1">
                          {ev.probability.markets.map((m, mi) => (
                            <div key={mi}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] font-data font-bold text-zinc-300 w-8">{m.asset}</span>
                                <div className="flex items-center gap-3 text-[9px] text-zinc-600">
                                  <span className="text-emerald-500">{m.up}% up</span>
                                  <span>{m.sideways}% flat</span>
                                  <span className="text-red-500">{m.down}% dn</span>
                                </div>
                              </div>
                              <ProbabilityBar up={m.up} down={m.down} sideways={m.sideways} />
                              <p className="text-[9px] text-zinc-600 mt-0.5">{m.note}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Liquidity Cycle Correlations ── */}
      {section === "liquidity" && (
        <div className="space-y-3" data-testid="astro-liquidity">

          {/* Liquidity Timeline Chart */}
          <LiquidityChart />

          <p className="text-xs text-zinc-500 mt-4 mb-3">How major planetary cycles have aligned with liquidity expansions and contractions</p>
          {LIQUIDITY_CORRELATIONS.map((lc, idx) => {
            const bgMap = { expansion: "border-emerald-800/30", transition: "border-amber-800/30", disruption: "border-red-800/30", restructuring: "border-violet-800/30" };
            const labelMap = { expansion: { bg: "bg-emerald-900/30", text: "text-emerald-400" }, transition: { bg: "bg-amber-900/30", text: "text-amber-400" }, disruption: { bg: "bg-red-900/30", text: "text-red-400" }, restructuring: { bg: "bg-violet-900/30", text: "text-violet-400" } };
            const style = labelMap[lc.type] || labelMap.transition;
            return (
              <div key={idx} className={`bg-zinc-900/50 border ${bgMap[lc.type]} rounded-sm p-4`} data-testid={`liquidity-${idx}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Lightning size={14} weight="bold" className={style.text} />
                  <span className="text-xs font-heading font-bold text-zinc-200">{lc.cycle}</span>
                  <Badge className={`text-[7px] px-1.5 py-0 border-0 h-3.5 ${style.bg} ${style.text}`}>{lc.type.toUpperCase()}</Badge>
                  <span className="text-[10px] font-data text-zinc-600 ml-auto">{lc.period}</span>
                </div>
                <p className="text-[12px] text-zinc-400 leading-relaxed">{lc.correlation}</p>
              </div>
            );
          })}

          {/* Key Insight */}
          <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-sm p-4 mt-4">
            <h4 className="text-sm font-heading font-bold text-zinc-200 mb-2">Key Pattern</h4>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Liquidity expansions (QE, rate cuts, fiscal stimulus) have consistently aligned with Jupiter transits through water signs and major planetary conjunctions. 
              The upcoming Jupiter in Cancer (Jun 2025 - Jun 2026) combined with the once-in-36-year Saturn-Neptune conjunction (Feb 2026) suggests a major liquidity event is approaching. 
              Historically, these alignments have preceded significant bull markets across both traditional and crypto assets.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
